import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ChevronRight, Building2, Save, ArrowLeft, Trash2, Plus, X,
  MapPin, Phone, Mail, Globe, FileText, Users, Briefcase, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'general', label: 'Informations', icon: Info },
  { id: 'legal', label: 'Infos légales', icon: FileText },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'billing', label: 'Facturation', icon: Briefcase },
];

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{label}</label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-2 mb-5">{children}</h3>
);

const EntrepriseDetailPage = () => {
  const { entrepriseId } = useParams();
  const navigate = useNavigate();
  const isNew = !entrepriseId || entrepriseId === 'nouveau';

  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', type: 'Établissement public', groupe: '', raison_sociale: '',
    code_interne: '', address: '', city: '', code_postal: '', pays: 'France FR',
    email: '', langue: 'Français', phone: '', site_web: '',
    tva: '', siret: '', code_ape: '', ville_rcs: '', forme_juridique: '',
    siren: '', representant_legal: '', email_representant: '',
    region: '', organisme_social: false, opco: '', numero_adherent_opco: '',
    effectifs: '', idcc: '', nace: '',
    adresses_facturation: [],
    contacts: [],
  });

  const fetchEntreprise = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/entreprises/${entrepriseId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, ...data }));
      } else {
        toast.error("Entreprise non trouvée");
        navigate('/donnees/entreprises');
      }
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [entrepriseId, isNew, navigate]);

  useEffect(() => { fetchEntreprise(); }, [fetchEntreprise]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/entreprises` : `${API}/entreprises/${entrepriseId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? 'Entreprise créée' : 'Entreprise mise à jour');
        if (isNew && data.entreprise_id) navigate(`/donnees/entreprises/${data.entreprise_id}`, { replace: true });
      } else { toast.error("Erreur lors de l'enregistrement"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cette entreprise ? Cette action est irréversible.")) return;
    await fetch(`${API}/entreprises/${entrepriseId}`, { method: 'DELETE', credentials: 'include' });
    toast.success("Entreprise supprimée");
    navigate('/donnees/entreprises');
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addContact = () => set('contacts', [...form.contacts, { nom: '', prenom: '', email: '', telephone: '', fonction: '' }]);
  const removeContact = (i) => set('contacts', form.contacts.filter((_, idx) => idx !== i));
  const updateContact = (i, key, val) => {
    const updated = [...form.contacts];
    updated[i] = { ...updated[i], [key]: val };
    set('contacts', updated);
  };

  const addBillingAddress = () => set('adresses_facturation', [...form.adresses_facturation, { label: '', address: '', city: '', code_postal: '', pays: 'France' }]);
  const removeBillingAddress = (i) => set('adresses_facturation', form.adresses_facturation.filter((_, idx) => idx !== i));
  const updateBillingAddress = (i, key, val) => {
    const updated = [...form.adresses_facturation];
    updated[i] = { ...updated[i], [key]: val };
    set('adresses_facturation', updated);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-slate-100 rounded animate-pulse" />
          <div className="h-12 w-96 bg-slate-100 rounded animate-pulse" />
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="entreprise-detail-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span className="cursor-pointer hover:text-violet-500 transition-colors" onClick={() => navigate('/donnees/entreprises')}>Entreprises</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">{isNew ? 'Nouvelle entreprise' : form.name}</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/donnees/entreprises')} className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-black text-slate-900" data-testid="entreprise-title">
                {isNew ? 'Nouvelle entreprise' : form.name}
              </h1>
              {!isNew && <Badge variant="outline" className="mt-1 text-xs">{form.type}</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && (
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="delete-entreprise-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-entreprise-btn">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl" data-testid="entreprise-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-violet-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.id === 'contacts' && form.contacts.length > 0 && (
                  <span className="bg-violet-100 text-violet-600 text-xs px-1.5 py-0.5 rounded-full">{form.contacts.length}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          {activeTab === 'general' && (
            <div className="space-y-8" data-testid="tab-content-general">
              <SectionTitle>Identification</SectionTitle>
              <div className="grid grid-cols-3 gap-6">
                <Field label="Nom de l'entreprise *" className="col-span-2">
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nom de l'entreprise" data-testid="input-name" />
                </Field>
                <Field label="Type">
                  <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none" value={form.type} onChange={e => set('type', e.target.value)} data-testid="select-type">
                    <option>Établissement public</option>
                    <option>Établissement privé</option>
                    <option>OPCO</option>
                    <option>Université</option>
                    <option>Association</option>
                    <option>Entreprise</option>
                    <option>Autre</option>
                  </select>
                </Field>
                <Field label="Raison sociale">
                  <Input value={form.raison_sociale} onChange={e => set('raison_sociale', e.target.value)} placeholder="Raison sociale" data-testid="input-raison-sociale" />
                </Field>
                <Field label="Groupe">
                  <Input value={form.groupe} onChange={e => set('groupe', e.target.value)} placeholder="Groupe" data-testid="input-groupe" />
                </Field>
                <Field label="Code interne">
                  <Input value={form.code_interne} onChange={e => set('code_interne', e.target.value)} placeholder="Code interne" data-testid="input-code-interne" />
                </Field>
              </div>

              <SectionTitle>Coordonnées</SectionTitle>
              <div className="grid grid-cols-3 gap-6">
                <Field label="Adresse" className="col-span-2">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Adresse" className="pl-10" data-testid="input-address" />
                  </div>
                </Field>
                <Field label="Code postal">
                  <Input value={form.code_postal} onChange={e => set('code_postal', e.target.value)} placeholder="75000" data-testid="input-code-postal" />
                </Field>
                <Field label="Ville">
                  <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Ville" data-testid="input-city" />
                </Field>
                <Field label="Région">
                  <Input value={form.region} onChange={e => set('region', e.target.value)} placeholder="Région" data-testid="input-region" />
                </Field>
                <Field label="Pays">
                  <Input value={form.pays} onChange={e => set('pays', e.target.value)} placeholder="France FR" data-testid="input-pays" />
                </Field>
                <Field label="Email">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@exemple.fr" className="pl-10" data-testid="input-email" />
                  </div>
                </Field>
                <Field label="Téléphone">
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+33 1 23 45 67 89" className="pl-10" data-testid="input-phone" />
                  </div>
                </Field>
                <Field label="Site web">
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={form.site_web} onChange={e => set('site_web', e.target.value)} placeholder="https://" className="pl-10" data-testid="input-site-web" />
                  </div>
                </Field>
              </div>

              <SectionTitle>Paramètres</SectionTitle>
              <div className="grid grid-cols-3 gap-6">
                <Field label="Langue">
                  <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none" value={form.langue} onChange={e => set('langue', e.target.value)} data-testid="select-langue">
                    <option>Français</option>
                    <option>English</option>
                  </select>
                </Field>
                <Field label="Représentant légal">
                  <Input value={form.representant_legal} onChange={e => set('representant_legal', e.target.value)} placeholder="Nom du représentant" data-testid="input-representant" />
                </Field>
                <Field label="Email du représentant">
                  <Input value={form.email_representant} onChange={e => set('email_representant', e.target.value)} placeholder="email@repr.fr" data-testid="input-email-representant" />
                </Field>
              </div>
            </div>
          )}

          {activeTab === 'legal' && (
            <div className="space-y-8" data-testid="tab-content-legal">
              <SectionTitle>Informations légales</SectionTitle>
              <div className="grid grid-cols-3 gap-6">
                <Field label="SIRET">
                  <Input value={form.siret} onChange={e => set('siret', e.target.value)} placeholder="123 456 789 00012" data-testid="input-siret" />
                </Field>
                <Field label="SIREN">
                  <Input value={form.siren} onChange={e => set('siren', e.target.value)} placeholder="123 456 789" data-testid="input-siren" />
                </Field>
                <Field label="N° TVA Intracommunautaire">
                  <Input value={form.tva} onChange={e => set('tva', e.target.value)} placeholder="FR12345678901" data-testid="input-tva" />
                </Field>
                <Field label="Code APE / NAF">
                  <Input value={form.code_ape} onChange={e => set('code_ape', e.target.value)} placeholder="8559A" data-testid="input-code-ape" />
                </Field>
                <Field label="Ville RCS">
                  <Input value={form.ville_rcs} onChange={e => set('ville_rcs', e.target.value)} placeholder="Paris" data-testid="input-ville-rcs" />
                </Field>
                <Field label="Forme juridique">
                  <Input value={form.forme_juridique} onChange={e => set('forme_juridique', e.target.value)} placeholder="SAS, SARL, etc." data-testid="input-forme-juridique" />
                </Field>
                <Field label="Code NACE">
                  <Input value={form.nace} onChange={e => set('nace', e.target.value)} placeholder="Code NACE" data-testid="input-nace" />
                </Field>
                <Field label="Code IDCC">
                  <Input value={form.idcc} onChange={e => set('idcc', e.target.value)} placeholder="Code IDCC" data-testid="input-idcc" />
                </Field>
                <Field label="Effectifs">
                  <Input value={form.effectifs} onChange={e => set('effectifs', e.target.value)} placeholder="Nombre de salariés" data-testid="input-effectifs" />
                </Field>
              </div>

              <SectionTitle>OPCO & Organisme social</SectionTitle>
              <div className="grid grid-cols-3 gap-6">
                <Field label="OPCO">
                  <Input value={form.opco} onChange={e => set('opco', e.target.value)} placeholder="Nom de l'OPCO" data-testid="input-opco" />
                </Field>
                <Field label="N° adhérent OPCO">
                  <Input value={form.numero_adherent_opco} onChange={e => set('numero_adherent_opco', e.target.value)} placeholder="Numéro adhérent" data-testid="input-numero-opco" />
                </Field>
                <Field label="Organisme social">
                  <div className="flex items-center gap-3 h-10">
                    <button
                      onClick={() => set('organisme_social', !form.organisme_social)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${form.organisme_social ? 'bg-violet-500' : 'bg-slate-300'}`}
                      data-testid="toggle-organisme-social"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.organisme_social ? 'translate-x-5' : ''}`} />
                    </button>
                    <span className="text-sm text-slate-600">{form.organisme_social ? 'Oui' : 'Non'}</span>
                  </div>
                </Field>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-6" data-testid="tab-content-contacts">
              <div className="flex items-center justify-between">
                <SectionTitle>Contacts de l'entreprise</SectionTitle>
                <Button onClick={addContact} variant="outline" size="sm" className="text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="add-contact-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> Ajouter un contact
                </Button>
              </div>

              {form.contacts.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center">
                  <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500 mb-4">Aucun contact ajouté pour cette entreprise</p>
                  <Button onClick={addContact} variant="outline" size="sm" className="text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="add-first-contact-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> Ajouter le premier contact
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {form.contacts.map((contact, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl p-5 relative group" data-testid={`contact-card-${i}`}>
                      <button onClick={() => removeContact(i)} className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-contact-${i}`}>
                        <X className="h-4 w-4" />
                      </button>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                          <span className="text-xs font-bold text-violet-600">{i + 1}</span>
                        </div>
                        <span className="text-sm font-semibold text-slate-700">Contact {i + 1}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <Field label="Nom">
                          <Input value={contact.nom} onChange={e => updateContact(i, 'nom', e.target.value)} placeholder="Nom" data-testid={`contact-${i}-nom`} />
                        </Field>
                        <Field label="Prénom">
                          <Input value={contact.prenom} onChange={e => updateContact(i, 'prenom', e.target.value)} placeholder="Prénom" data-testid={`contact-${i}-prenom`} />
                        </Field>
                        <Field label="Fonction">
                          <Input value={contact.fonction} onChange={e => updateContact(i, 'fonction', e.target.value)} placeholder="Fonction" data-testid={`contact-${i}-fonction`} />
                        </Field>
                        <Field label="Email">
                          <Input value={contact.email} onChange={e => updateContact(i, 'email', e.target.value)} placeholder="email@exemple.fr" data-testid={`contact-${i}-email`} />
                        </Field>
                        <Field label="Téléphone">
                          <Input value={contact.telephone} onChange={e => updateContact(i, 'telephone', e.target.value)} placeholder="+33 ..." data-testid={`contact-${i}-telephone`} />
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6" data-testid="tab-content-billing">
              <div className="flex items-center justify-between">
                <SectionTitle>Adresses de facturation</SectionTitle>
                <Button onClick={addBillingAddress} variant="outline" size="sm" className="text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="add-billing-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> Ajouter une adresse
                </Button>
              </div>

              {form.adresses_facturation.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center">
                  <Briefcase className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500 mb-4">Aucune adresse de facturation</p>
                  <Button onClick={addBillingAddress} variant="outline" size="sm" className="text-violet-600 border-violet-200 hover:bg-violet-50" data-testid="add-first-billing-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> Ajouter une adresse
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {form.adresses_facturation.map((addr, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl p-5 relative group" data-testid={`billing-card-${i}`}>
                      <button onClick={() => removeBillingAddress(i)} className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-billing-${i}`}>
                        <X className="h-4 w-4" />
                      </button>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Libellé" className="col-span-2">
                          <Input value={addr.label} onChange={e => updateBillingAddress(i, 'label', e.target.value)} placeholder="Ex: Siège social" data-testid={`billing-${i}-label`} />
                        </Field>
                        <Field label="Adresse" className="col-span-2">
                          <Input value={addr.address} onChange={e => updateBillingAddress(i, 'address', e.target.value)} placeholder="Adresse" data-testid={`billing-${i}-address`} />
                        </Field>
                        <Field label="Ville">
                          <Input value={addr.city} onChange={e => updateBillingAddress(i, 'city', e.target.value)} placeholder="Ville" data-testid={`billing-${i}-city`} />
                        </Field>
                        <Field label="Code postal">
                          <Input value={addr.code_postal} onChange={e => updateBillingAddress(i, 'code_postal', e.target.value)} placeholder="Code postal" data-testid={`billing-${i}-code-postal`} />
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EntrepriseDetailPage;
